import { toast } from 'react-toastify'
import { useParams, useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'

import { FormSection } from 'components/Form'
import { DestroyButton } from '../components/Destroy'
import { PageHeader } from 'components/PageHeader'
import { Close, Button } from 'components/Buttons'
import { LoadingCircle } from 'components/Loading'
import { ProtectedSection } from 'components/ProtectedSection'
import { ClientSearch, PlanSearch } from '../components/Searchs'
import { DatePickerWithError } from 'components/FormInputs/DatePicker'
import {
  InputWithAddon,
  InputWithValidationError,
  RadioListWithDescription,
  SimpleCustomSelect
} from 'components/FormInputs'
import { useConfirmationDialog } from 'components/ConfirmationDialog/useConfirmationDialog'
import { CreateInstallment } from '../components/Installments'

import { Errors } from 'types'
import { check } from 'utils/error'
import { validateUpdate as validate } from '../validation'
import { edit, newShow } from '../services'
import {
  amount as formatAmount,
  currency,
  formatCurrency,
  cleanAmount
} from 'utils'
import { Client, Installment, Order, Plan, Role } from 'models'
import { isCurrentMonth } from '../helpers/utils'
import { getCurrentDateParts, getFirstDayOfMonth } from 'utils/datetime'
import { ReadResult } from 'interfaces/queryOptions'
import { shippingMethods } from '../helpers/constants'
import { fineModes } from 'components/FormInputs/options'
import { Coupon } from 'models/coupon'
import { CouponSearch } from '../components/Searchs/Coupon'
import { list as listInvoices } from 'modules/Invoices/services/read'
import { format } from 'date-fns'

export function Edit() {
  const { orderId } = useParams() as unknown as { orderId: number }

  const navigate = useNavigate()

  const [order, setOrder] = useState<ReadResult<Order>>()
  const [client, setClient] = useState<ReadResult<Client>>()
  const [plan, setPlan] = useState<ReadResult<Plan>>()

  const [coupon, setCoupon] = useState<ReadResult<Coupon>>()

  const [shouldClear, setShouldClear] = useState(false)

  const [orderedAt, setOrderedAt] = useState(new Date())
  const [amount, setAmount] = useState('0,00')

  const [errors, setErrors] = useState<Errors>({})

  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const [installments, setInstallments] = useState<Installment[]>([])
  const [installmentsHasError, setInstallmentsHasError] = useState(false)
  const [hadOriginalInstallments, setHadOriginalInstallments] = useState(false)
  const [isClientInvoiceInDraftStatus, setIsClientInvoiceInDraftStatus] =
    useState(false)

  const [showConfirmationDialog] = useConfirmationDialog()

  useEffect(() => {
    const fetch = async () => {
      if (!orderId) return

      setIsLoading(true)
      try {
        const options = {
          includes: {
            client: [
              'id',
              'account_name',
              'document',
              'document_type',
              'billing_cycle_day'
            ],
            plan: [
              'id',
              'name',
              'nature',
              'amount',
              'currency',
              'duration',
              'interval',
              'restricted',
              'country_id',
              'recurring',
              'penalty_period',
              'meta'
            ],
            coupon: ['*'],
            order_installments: ['*'],
            product: ['*']
          }
        }
        const result = await newShow(orderId, options)

        setOrder({
          ...result,
          attributes: {
            ...result.attributes,
            ...(result.attributes.fine_amount && {
              fine_amount: formatAmount(result.attributes.fine_amount)
            })
          }
        })

        const orderInstallments = result.relationships?.order_installments
        if (orderInstallments?.length) {
          setHadOriginalInstallments(true)
          setInstallments(
            orderInstallments.map(({ attributes }: any) => ({
              type: attributes.type,
              amount: attributes.amount,
              due_at: new Date(attributes.due_at),
              upfront: attributes.upfront,
              eligible: attributes.eligible
            }))
          )
        }

        const clientData = result.relationships?.client
        if (clientData?.attributes?.billing_cycle_day) {
          const billingCycleDay = clientData.attributes.billing_cycle_day
          const { year, month } = getCurrentDateParts()
          const dueDate = new Date(year, month, billingCycleDay)
          const formattedDate = format(dueDate, 'yyyy-MM-dd')
          const invoices = await listInvoices({
            includes: { statuses: ['status'] },
            filters: [
              { key: 'client_id', op: 'eq', value: clientData.id },
              { key: 'due_at', op: 'eq', value: formattedDate }
            ]
          })
          setIsClientInvoiceInDraftStatus(
            invoices.data.some(
              (inv: any) =>
                inv.relationships?.statuses?.[0]?.attributes?.status === 'draft'
            )
          )
        }

        setAmount(formatAmount(result.attributes.amount))
        setOrderedAt(new Date(result.attributes.ordered_at ?? new Date()))
        setClient(result.relationships?.client)
        setPlan({
          ...result.relationships?.plan,
          relationships: {
            country: { id: result.relationships?.plan.attributes.country_id },
            product: result.relationships?.product
          }
        })

        setCoupon(result.relationships?.coupon ?? null)

        setIsLoading(false)
      } catch (err: any) {
        console.error(err)
        navigate('/orders')
        toast.error(
          err.suggestedMessage ?? 'Falha ao buscar os dados do pedido'
        )
        setIsLoading(false)
      }
    }
    fetch()
  }, [orderId])

  const performSave = useCallback(async () => {
    if (!order || !plan || !client) return

    order.attributes.ordered_at = isCurrentMonth(orderedAt)
      ? orderedAt
      : undefined
    order.attributes.amount = Math.round(+amount.replace(',', '.') * 100)
    if (!order.attributes.fine_amount) {
      order.attributes.fine_amount = 0
    }

    if (!plan.attributes.recurring) {
      order.attributes.fine_mode = 'fixed'
      order.attributes.fine_amount = 0
    }

    setIsSaving(true)
    try {
      await edit(orderId, {
        order,
        client,
        plan,
        coupon,
        installments: hadOriginalInstallments
          ? installments
          : installments.length > 0
          ? installments
          : undefined
      })
      toast.success('Pedido editado com sucesso')
      setIsSaving(false)
    } catch (err: any) {
      setIsSaving(false)
      toast.error(
        err.suggestedMessage ?? 'Houve uma falha ao salvar as alterações'
      )
    }
  }, [client, amount, orderId, order, orderedAt, plan, coupon, installments])

  const onSave = useCallback(async () => {
    if (!order || !plan || !client) return

    if (check(errors)) return

    if (installmentsHasError) return

    if (hadOriginalInstallments) {
      showConfirmationDialog({
        title: 'Atenção: pedido parcelado',
        message:
          installments.length > 0
            ? 'Este pedido possui parcelas que podem ter gerado faturas. As parcelas existentes serão substituídas pelas novas. Deseja continuar?'
            : 'Este pedido possui parcelas que podem ter gerado faturas. As parcelas existentes serão removidas. Deseja continuar?',
        onConfirm: () => {
          void performSave()
        }
      })
    } else {
      await performSave()
    }
  }, [
    errors,
    hadOriginalInstallments,
    installmentsHasError,
    performSave,
    showConfirmationDialog,
    order,
    plan,
    client
  ])

  const onAttributeChange = useCallback(
    (attr: keyof Order, value: any): void => {
      if (!order) return

      if (attr === 'quantity') {
        setInstallments([])
      }

      const context =
        attr === 'penalty_period'
          ? { duration: plan?.attributes.duration }
          : undefined
      const error = validate(attr as any, value, context)

      if (attr === 'fine_amount' && value === '') {
        setOrder({
          ...order,
          attributes: {
            ...order.attributes,
            fine_amount: '',
            fine_mode: null as unknown as string
          }
        })
        return
      }

      if (attr === 'fine_amount') {
        value = formatCurrency(value)
      }

      setOrder({
        ...order,
        attributes: { ...order.attributes, [attr]: value }
      })
      setErrors({ ...errors, [attr]: error })
    },
    [errors, order, plan]
  )

  const onAmountChange = useCallback((value: string) => {
    if (value === '-0,00' || value === '-0,0') {
      setAmount('0,00')
    } else {
      const newAmount = formatAmount(value.replace(/[^0-9]/g, ''))
      value.includes('-') ? setAmount('-' + newAmount) : setAmount(newAmount)
    }
    setInstallments([])
  }, [])

  const onOrderedAtChange = useCallback((value: string) => {
    setOrderedAt(new Date(value))
  }, [])

  const onCouponSelection = useCallback((coupon: any): void => {
    setCoupon(coupon)
  }, [])

  return (
    <>
      <PageHeader title="Pedidos" action="Edição" />

      {isLoading ? (
        <LoadingCircle />
      ) : (
        <div className="w-3/5 px-4 animate-fade-in-down">
          <h3 className="text-xl mb-5 leading-6 font-medium text-gray-900">
            Pedido: {order?.attributes.token}
          </h3>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              onSave()
            }}
          >
            <FormSection title="Cliente">
              <ClientSearch
                value={client}
                onSelect={setClient}
                error={errors?.client_id}
              />
            </FormSection>

            <FormSection title="Plano">
              <PlanSearch
                clear={false}
                value={plan}
                onSelect={(p: ReadResult<Plan>) => {
                  setPlan(p)
                  setInstallments([])
                }}
                onChange={(p: ReadResult<Plan>) => {
                  setPlan(p)
                  setInstallments([])
                }}
                error={errors?.plan_id}
              />
            </FormSection>

            <FormSection title="Pedido">
              <div className="flex justify-between gap-8">
                <div className="w-1/2">
                  <InputWithValidationError
                    label="Quantidade"
                    type="number"
                    name="quantity"
                    min="1"
                    value={order?.attributes.quantity}
                    onChange={(value) => onAttributeChange('quantity', value)}
                    error={errors?.quantity}
                    onWheel={(e: React.FocusEvent<HTMLInputElement>) =>
                      e.target.blur()
                    }
                  />
                </div>

                <div className="w-1/2">
                  <DatePickerWithError
                    label="Pedido em"
                    name="orderedAt"
                    date={orderedAt}
                    minDate={getFirstDayOfMonth()}
                    maxDate={new Date()}
                    onChange={(value: any) => onOrderedAtChange(value)}
                  />
                </div>
              </div>

              <div className="flex justify-between gap-8">
                <div className="w-1/2">
                  <InputWithAddon
                    label="Valor"
                    type="text"
                    frontAddon={currency(plan?.attributes.currency)}
                    disabled={plan?.attributes.restricted}
                    name="amount"
                    value={amount}
                    onChange={(value) => onAmountChange(value)}
                  />
                </div>

                <div className="w-1/2">
                  <label className="block text-left text-sm font-medium text-gray-700">
                    Diferença
                  </label>
                  <div className="mt-1 h-10 w-full text-left font-medium text-gray-600 border rounded-md py-2 px-2 focus:outline-none bg-gray-50">
                    {plan
                      ? `${
                          plan.attributes.amount !== cleanAmount(amount)
                            ? (plan.attributes.amount as number) >
                              cleanAmount(amount)
                              ? '-'
                              : '+'
                            : ''
                        } ${currency(plan.attributes.currency)} ${formatAmount(
                          Math.abs(
                            (plan.attributes.amount as number) -
                              cleanAmount(amount)
                          )
                        )}`
                      : ''}
                  </div>
                </div>
              </div>

              {plan && plan.attributes.recurring && (
                <div className="flex flex-row space-x-8">
                  <div className="w-1/3">
                    <SimpleCustomSelect
                      label="Tipo de multa"
                      options={fineModes}
                      value={order?.attributes.fine_mode ?? ''}
                      onChange={(fineModes) =>
                        onAttributeChange('fine_mode', fineModes)
                      }
                    />
                  </div>

                  <div className="w-1/3">
                    <InputWithAddon
                      label="Valor da multa (por unidade)"
                      name="fine_amount"
                      type="text"
                      pattern="[0-9]+([\.,][0-9]+)?"
                      value={order?.attributes.fine_amount}
                      frontAddon={
                        order?.attributes.fine_mode === 'fixed'
                          ? currency(plan?.attributes.currency)
                          : '%'
                      }
                      onChange={(value) =>
                        onAttributeChange('fine_amount', value)
                      }
                      error={errors.amount}
                    />
                  </div>
                </div>
              )}

              {plan &&
                plan.attributes.recurring &&
                order?.attributes.fine_mode &&
                plan.attributes.interval === 'month' &&
                plan.attributes.penalty_period != null && (
                  <div className="flex flex-col space-y-2">
                    <div className="w-1/3">
                      <InputWithValidationError
                        label={`Período de penalidade${
                          plan.attributes.duration
                            ? ` (máx. ${plan.attributes.duration} meses)`
                            : ''
                        }`}
                        name="penalty_period"
                        type="text"
                        inputMode="numeric"
                        value={order?.attributes.penalty_period ?? ''}
                        onChange={(value) =>
                          onAttributeChange(
                            'penalty_period',
                            value.replace(/\D/g, '') || null
                          )
                        }
                        error={errors.penalty_period}
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      Durante esse período, multas de cancelamento são cobradas
                      normalmente. Após ele, o cancelamento não gera mais multa.{' '}
                      <span className="text-amber-600">
                        Se vazio, multas poderão ser cobradas a qualquer
                        momento, sem prazo de encerramento.
                      </span>
                    </p>
                  </div>
                )}

              {plan &&
                client &&
                plan.attributes.recurring === false &&
                plan.attributes.meta?.installments?.find(
                  (inst: any) =>
                    inst.type === plan.relationships?.product?.attributes?.type
                ) &&
                cleanAmount(amount) !== 0 && (
                  <CreateInstallment
                    type="non_recurring"
                    totalAmount={
                      cleanAmount(amount) * (order?.attributes.quantity ?? 1)
                    }
                    amountCurrency={plan.attributes.currency as string}
                    billingCycleDay={
                      client.attributes.billing_cycle_day as number
                    }
                    setInstallments={setInstallments}
                    onError={setInstallmentsHasError}
                    maxNumberOfInstallments={
                      plan.attributes.meta.installments.find(
                        (inst: any) =>
                          inst.type ===
                          plan.relationships?.product?.attributes?.type
                      ).requirements.max_installment_number
                    }
                    clientInvoiceInDraftStatus={isClientInvoiceInDraftStatus}
                    defaultInstallments={
                      installments.length > 0 ? installments : undefined
                    }
                  />
                )}

              {plan?.attributes.recurring && (
                <FormSection title="Cupom">
                  <CouponSearch
                    clear={shouldClear}
                    setClear={setShouldClear}
                    onSelect={onCouponSelection}
                    value={coupon}
                    error={errors.coupon}
                    plan={plan}
                  />
                </FormSection>
              )}

              <RadioListWithDescription
                label="Método de envio"
                options={shippingMethods}
                value={order?.attributes.shipping_method}
                onChange={(value) =>
                  onAttributeChange('shipping_method', value)
                }
              />
            </FormSection>

            <div className="mt-4 flex space-x-4 justify-end">
              <ProtectedSection
                roles={[Role.MANAGER, Role.SALES, Role.FINANCE]}
              >
                <DestroyButton id={orderId} />
              </ProtectedSection>

              <Close linkTo="/orders" />
              <Button
                type="submit"
                label="Salvar"
                disabled={isSaving || installmentsHasError}
              />
            </div>
          </form>
        </div>
      )}
    </>
  )
}
